import { arrayOrArray } from '@apia/util';
import { ParameterRenderer } from './types';
import { useRef, ReactNode, Suspense, useMemo } from 'react';
import {
  Checkbox,
  FieldErrorMessage,
  FieldLabel,
  importComponent,
} from '../components';
import { Parameter } from './Parameter';
import { Heading, Input, Select, Textarea } from '@apia/theme';
import { observer } from 'mobx-react-lite';

export const ParameterRender = observer(
  ({
    index = 0,
    omitLabel = false,
    parameter,
  }: {
    index: number;
    omitLabel?: boolean;
    parameter: Parameter;
  }) => {
    const Label = useMemo(
      () =>
        omitLabel && parameter.state.type !== 'check'
          ? ({ children }: { children: ReactNode }) => <>{children}</>
          : FieldLabel,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const error = parameter.state.validationError && (
      <FieldErrorMessage name={parameter.state.name}>
        {parameter.state.validationError}
      </FieldErrorMessage>
    );
    const componentRef = useRef<ParameterRenderer | null>(null);

    if (parameter.component) {
      componentRef.current =
        componentRef.current ||
        (importComponent(parameter.component!) as ParameterRenderer);

      return (
        <Suspense fallback={null}>
          <componentRef.current parameter={parameter} index={index} />
          {error}
        </Suspense>
      );
    } else if (parameter.state.type === 'input') {
      return (
        <Label
          label={parameter.state.title}
          required={parameter.state.required}
          title={parameter.state.description}
        >
          <Input
            name={parameter.state.name}
            title={parameter.state.description}
            onChange={(ev) => {
              parameter.setValue(ev.target.value, index);
            }}
            value={parameter.state.values[index] ?? ''}
            className={
              parameter.state.validationError ? 'requiredFilter__Border' : ''
            }
          />
          {error}
        </Label>
      );
    } else if (parameter.state.type === 'text_area') {
      return (
        <Label
          label={parameter.state.title}
          required={parameter.state.required}
          title={parameter.state.description}
        >
          <Textarea
            name={parameter.state.name}
            title={parameter.state.description}
            onChange={(ev) => {
              const values: string[] = [];
              let lastIndex = 0;
              do {
                const finishIndex = Math.min(
                  lastIndex + 240,
                  ev.target.value.length,
                );
                values.push(ev.target.value.slice(lastIndex, finishIndex));
                lastIndex += 240;
              } while (lastIndex < ev.target.value.length);

              parameter.setValues(
                values.map((x) => (x !== null ? x : '')),
                index,
              );
            }}
            value={parameter.state.values.join('')}
            className={
              parameter.state.validationError ? 'requiredFilter__Border' : ''
            }
          />
          {error}
        </Label>
      );
    } else if (parameter.state.type === 'check') {
      return (
        <Label
          label={parameter.state.title}
          required={parameter.state.required}
          title={parameter.state.description}
        >
          <Checkbox
            name={parameter.state.name}
            title={parameter.state.description}
            onChange={(ev) => {
              parameter.setValue(String(ev.target.checked), index);
            }}
            checked={parameter.state.values[index] === 'true'}
            className={
              parameter.state.validationError ? 'requiredFilter__Border' : ''
            }
          />
          {error}
        </Label>
      );
    } else if (parameter.state.type === 'combo') {
      return (
        <Label
          label={parameter.state.title}
          required={parameter.state.required}
          title={parameter.state.description}
        >
          <Select
            name={parameter.state.name}
            title={parameter.state.description}
            onChange={(ev) => {
              parameter.setValue(ev.target.value, index);
            }}
            value={parameter.state.values[index]}
            className={
              parameter.state.validationError ? 'requiredFilter__Border' : ''
            }
          >
            {arrayOrArray(parameter.state.possibleValues).map((c) => (
              <option value={c.value}>{c.text}</option>
            ))}
          </Select>
          {error}
        </Label>
      );
    } else if (parameter.state.type === 'mdl_attribute') {
      console.warn('There is no renderer for type mdl_attribute');
      return null;
    } else if (parameter.state.type === 'mdl_doc_type') {
      console.warn('There is no renderer for type mdl_doc_type');
      return null;
    } else if (parameter.state.type === 'mdl_metadata') {
      console.warn('There is no renderer for type mdl_metadata');
      return null;
    } else if (parameter.state.type === 'mdl_entity') {
      console.warn('There is no renderer for type mdl_entity');
      return null;
    } else if (parameter.state.type === 'mdl_ai_connector') {
      return null;
    } else if (parameter.state.type === 'mdl_ai_completion_model') {
      return null;
    } else if (parameter.state.type === 'mdl_ai_embeddings_model') {
      return null;
    } else if (parameter.state.type === 'hidden') {
      return null;
    } else if (parameter.state.type === 'label') {
      return (
        <Heading
          sx={{ my: 5, fontSize: '19px !important' }}
          title={parameter.state.description}
          as="h5"
        >
          {parameter.state.title}
        </Heading>
      );
    }

    console.warn('Cannot render parameter of type', parameter.state.type);

    return null;
  },
);
